/**
 * Every hunt setting, described once.
 *
 * ── Why a registry and not just JSX ─────────────────────────────────
 * This page carries forty numbers across six sections. Written as forty inline
 * `<Setting label=… hint=…>` blocks — which is what it was — each one's text,
 * its unit, and its allowed range lived at the single point it happened to be
 * rendered, so nothing else could see them: there was no way to search for a
 * setting, no way to mark which section held an unsaved change, and no way to
 * tell an operator that the 900 they typed would be refused before they pressed
 * Update.
 *
 * Describing each field once, here, gives all three for free and keeps the JSX
 * about STRUCTURE — which cards exist, which are conditional, which derived
 * lines sit between them.
 *
 * ── The two-level explanation ───────────────────────────────────────
 * `summary` is one sentence: what this number does. It is always on screen.
 * `detail` is everything else — the reasoning, the cost, the failure it
 * prevents — and it is one click away. Nothing was deleted in the move; the
 * long-form prose that used to be the hint is the detail now. The split exists
 * because the two answer different questions, and an operator who came here to
 * change one number should not have to read a paragraph to find out whether
 * this is the number.
 *
 * ── The bounds are the API's, restated ──────────────────────────────
 * `min`/`max`/`step` mirror `HuntSettingsPatch` in `apps/api/src/routes/
 * automation.ts` exactly. That schema REJECTS an out-of-range value with a 400
 * rather than clamping it, so the form can say so before the round trip — but
 * only if the two agree. They are checked against each other whenever either
 * moves; a divergence here shows up as a save that is refused for a reason the
 * form said was fine.
 */

/* ------------------------------------------------------------------ *
 * Wire shapes
 * ------------------------------------------------------------------ */

/**
 * One live hunt, as COUNTERS only.
 *
 * This page lists every running hunt and polls that list every two seconds. It
 * used to receive each hunt's full ring of recent checks, response bodies
 * included — so at a hundred concurrent customers it was shipping thousands of
 * poll records a minute to render rows of numbers, none of which displayed any
 * of it. The per-check detail lives in the drawer, which asks for exactly one
 * hunt.
 */
export interface HuntRow {
  customerId: string
  scheduleId: string
  state: string
  polls: number
  errors: number
  lastStatus: string | null
  elapsedMs: number
  /** Measured, not configured — with no gap the rate is set by the round trip. */
  rate: number
  meanRttMs: number
  /** Distinct ISP endpoints this hunt's workers are spread across. */
  egress: number
  /** Borrowed accounts currently held across every worker's ring. */
  accounts: number
  /** How many workers were launched. */
  workers: number
}

export interface HuntLive {
  tracked: number
  hunts: HuntRow[]
}

/** What is left in the shared account pool, so the budget is visible. */
export interface PoolStats {
  free: number
  held: number
  ready: boolean
}

/**
 * What is left in the shared ISP endpoint pool.
 *
 * Five numbers where the account pool needs two, because an ISP row has a third
 * state neither of the others has: `wafCooldownUntil` benches an address after
 * a WAF challenge, and a benched row is neither free nor held by anyone — it is
 * gone from the usable pool until its clock runs out.
 *
 * That is the state that has actually emptied this pool in production. The
 * field is shared with the other system, so a bad afternoon over there benches
 * rows for both, and the pool reads as exhausted while almost nothing is held.
 * A free count alone cannot explain that; `cooling` and `others` can.
 */
export interface IspPoolStats {
  /** Claimable right now: nobody holds it and it is not benched. */
  free: number
  /** Held by this console. */
  held: number
  /** Held by the system that shares this collection. */
  others: number
  /** Benched on a WAF cooldown, whoever benched it. */
  cooling: number
  total: number
  ready: boolean
}

export interface HuntSettingsDto {
  enabled: boolean
  /**
   * Which country's jobs everything after sign-in runs against.
   *
   * Sign-in itself is pinned to US on the server and does not follow this.
   */
  market: string
  /**
   * Where the shift confirm goes, independent of `market`.
   *
   * `'auto'` follows the jobs country and is the default. A market code pins
   * update-application / job-confirm and its heartbeat to that country's portal
   * while every other call stays on `market`. The ids in the body never follow.
   */
  confirmMarket: string
  workersPerCustomer: number
  /**
   * The GraphQL shift-list fleet's default count.
   *
   * On the Current flow these run only while the assigned shift has no UUID:
   * they read the job's shift list to find it, then stand down. They never
   * claim. Floored at 1 on the server, so the fleet that reads the UUID always
   * exists.
   */
  graphqlWorkersPerCustomer: number
  /** How many calls each shift-list worker keeps. Separate Redis budget. */
  graphqlWorkerLogCalls: number
  /** Whether the wizard sits Amazon's recorded assessment. See the field below. */
  assessmentEnabled: boolean
  /**
   * Consecutive refusals — HTTP 429, 403 or 502 — before the schedule watch
   * stops sending update calls. 0 = never stop. FAILED_UPDATE_APPLICATION does
   * not count; the name predates that rule.
   */
  failedUpdateStreakMax: number
  /**
   * SELECTED_SCHEDULE_NOT_AVAILABLE replies over the whole hunt before the
   * current flow gives the shift up. 0 = never.
   */
  claimUnavailableMax: number
  /**
   * Consecutive schedule-worker reads with no free seat before the hunt gives
   * the shift up. 0 = off. Current flow only.
   */
  seatEmptyReads: number
  /**
   * HTTP 429s in a row on current-flow claims before the customer is parked.
   * 0 = never park. Not reset by the shift closing — only by another answer.
   */
  rateLimitParkAfter: number
  /** How long that park lasts, in ms. One minute to two hours. */
  rateLimitParkMs: number
  /** Minimum gap between two WATCH claims on the SAME opening, in ms. 0 = off. */
  claimGapMs: number
  /**
   * How long schedule workers withhold further confirms after the hunt's FIRST
   * ACTIVE — which itself fires one — in ms. 0 = off. Current flow only; once
   * per hunt.
   */
  firstActiveHoldMs: number

  /* --- the claim's SHAPE: one confirm, or the v1/workflow burst --- */
  /** Which sequence runs once a detector has seen the shift open. */
  huntFlow: string
  /** `block` | `numeric` | `park` — what a run does when its shift has no UUID. */
  brazilUuidMissing: string
  /** Let an auto-picked Brazil run hunt a shift that has no shift UUID yet. */
  brazilNoUuidFlow: boolean
  /** Name the current flow's job by UUID on the create, and its shift by UUID on the confirms. */
  currentUuidFlow: boolean
  /** `body` | `numeric` — which id form the Brazil Referer carries. */
  brazilRefererIds: string
  /** How long the burst waits after ACTIVE before its first call, in ms. */
  burstWaitMs: number
  /** Iterations one burst may run. Bounds calls; the burst has its own clock. */
  burstMaxLoops: number
  /** v1 creates per burst iteration. */
  burstCreatesPerLoop: number
  /** Confirms per burst iteration. */
  burstUpdatesPerLoop: number
  /** Pause between burst iterations, in ms. 0 is back-to-back. */
  burstGapMs: number
  /** Creates the burst may spend obtaining an application before giving up. */
  burstCreateAttempts: number
  /** Consecutive `SELECTED_SCHEDULE_NOT_AVAILABLE` replies that end a burst. */
  burstUnavailableStreakMax: number
  /** How long a job-only burst waits after ACTIVE before its first confirm, ms. */
  burstNoUuidWaitMs: number
  /** The gap between two confirms in a job-only burst, drawn from [min, max] ms. */
  burstNoUuidGapMinMs: number
  burstNoUuidGapMaxMs: number
  /** Refusals that end a job-only burst. 1 — the default — is also the floor. */
  burstNoUuidUnavailableStreakMax: number
  /** Wait after a refused seat before this customer may burst again, in ms. */
  burstUnavailableCooldownMs: number
  /** Wait after losing an auto-picked shift before looking for another, in ms. */
  burstReassignDelayMs: number
  /** Brazil flow: re-pick at the end of a burst if the watch read no free seat. */
  repickWhenSeatsGone: boolean
  /** Bot-check solves one burst may buy. 0 removes the limit. */
  burstSolveBudget: number
  /** File one burst call in every N to the claim log. 0 files every call. */
  burstLogEvery: number
  /** Burst calls in flight across the whole fleet at once. */
  burstFleetConcurrency: number
  /** MINIMUM gap between one worker's checks. 0 — the default — means none. */
  pollIntervalMs: number
  /** How often the schedule list is re-read for a customer with no job yet. */
  scheduleMatchPollMs: number
  /**
   * The DAY half of the clock, in minutes past midnight. Night is whatever is
   * left over. Matched against the customer's shift priority when a full-time
   * shift is picked for them automatically.
   */
  dayShiftStartMin: number
  dayShiftEndMin: number
  maxHuntMs: number
  /** Borrowed identities each worker cycles through. 0 uses the customer's own. */
  sessionAccountsPerWorker: number
  /** Consecutive 403s one borrowed account may earn before the ring swaps it. */
  accountRotateAfter403: number
  /** Calls each worker keeps for its own log. 0 turns it off. */
  workerLogCalls: number
  /** The random wait between asks for an orientation slot, after a shift is won. */
  nheJitterMinMs: number
  nheJitterMaxMs: number
  /** How long a signed-in session stays usable, and when it re-mints itself. */
  sessionMaxAgeMs: number
  /** Re-sign-in at this age, while the current session still works. 0 = off. */
  sessionRenewMs: number
  /** How often the seat is re-confirmed. 0 turns the keep-alive call off. */
  keepAliveMs: number
  /** Onboarding gives up after this long with NO progress. 0 = never. */
  onboardingWatchdogMs: number
  /** The GA fleet's default count — `get-all-schedules` readers. */
  schedulesWorkersPerCustomer: number
  schedulesPollMs: number
  schedulesAccountsPerWorker: number
  /** Gap between two asks for a customer's identity-verification LINK. 0 = none. */
  idvLinkGapMs: number
  /** Gap between two customer launches when a saved roster is resumed. 0 = none. */
  resumeStaggerMs: number
  live: HuntLive
  pool: PoolStats
  /** The ISP endpoint pool — the OTHER ceiling on concurrent customers. */
  ispPool: IspPoolStats
}

/**
 * The one query key for this page.
 *
 * Shared rather than restated: the pool-release tool invalidates it so the free
 * count under the button updates in the same beat as the release, and a second
 * literal spelled the same way is a cache that silently stops refreshing the
 * day one of them is edited.
 */
export const HUNT_SETTINGS_KEY = ['hunt-settings'] as const

/** The markets the server accepts. Mirrors `MARKETS` in the API's hiring config. */
export const MARKETS = [
  { code: 'CA', label: 'Canada', host: 'hiring.amazon.ca' },
  { code: 'US', label: 'United States', host: 'hiring.amazon.com' },
] as const

export function marketLabel(code: string): string {
  return MARKETS.find((m) => m.code === code)?.label ?? code
}

export function marketHost(code: string): string {
  return MARKETS.find((m) => m.code === code)?.host ?? code
}

/**
 * Every half hour of the clock, as `{ value: minutes, label: '5:00 AM' }`.
 *
 * The two day-window fields are NUMBERS on the wire — minutes past midnight —
 * but a box asking for `300` is a box nobody can fill in correctly. A select of
 * real clock times is the same setting stated in the units it was decided in,
 * and the draft is a string mirror anyway, so `toPayload`'s `Number(...)` turns
 * it back with no new machinery.
 *
 * Half-hourly, at 48 entries, because every shift boundary worth choosing sits
 * on one — and a full 1,440-entry minute list is a scroll, not a choice. The
 * server still accepts any minute, so a value set by hand or by an older build
 * survives a round trip through the form: `sameOrCustom` below keeps it.
 */
export const CLOCK_OPTIONS: readonly SelectOption[] = Array.from({ length: 48 }, (_, i) => {
  const minute = i * 30
  const h24 = Math.floor(minute / 60)
  const mm = String(minute % 60).padStart(2, '0')
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return { value: String(minute), label: `${h12}:${mm} ${h24 < 12 ? 'AM' : 'PM'}` }
})

/** `300` -> `'5:00 AM'`. Mirrors `clockLabel` in the API's `shift-band.ts`. */
export function clockLabel(minute: number): string {
  const m = ((Math.trunc(minute) % 1_440) + 1_440) % 1_440
  const h24 = Math.floor(m / 60)
  const mm = String(m % 60).padStart(2, '0')
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${mm} ${h24 < 12 ? 'AM' : 'PM'}`
}

/* ------------------------------------------------------------------ *
 * Sections
 * ------------------------------------------------------------------ */

/**
 * The sections, in the order the work actually happens: choose a country, look
 * for the shift, claim it, finish the application, bound the run, watch it go.
 *
 * ── Why these six and not the previous seven ────────────────────────
 * The old split was by SUBSYSTEM — "Watching", "Searching", "Job record" — and
 * named each tab after the internal fleet behind it. Three of the seven tabs
 * were therefore three different words for "look for the shift", and which was
 * which was, by the page's own admission, the single most common thing to get
 * wrong on it. They are one question and they are now one tab, with each fleet
 * as a clearly named card inside it, showing whether it is on.
 *
 * `blurb` is not decoration. A tab label can only ever be two words; the blurb
 * is where the tab says what it is for, and it is on screen rather than in a
 * `title` attribute, because a tooltip is invisible to anybody who is not
 * already hovering the thing they were unsure about.
 */
export const TABS = [
  {
    id: 'market',
    label: 'Market',
    blurb: 'Which country’s jobs we hunt, and which portal the claim is sent to.',
  },
  {
    id: 'find',
    label: 'Finding',
    blurb:
      'Schedule workers watch for the shift to open, and they are the only fleet that claims. GraphQL and GA workers read the job’s listings instead — for the shift’s UUID while it is missing, and, before an application exists, for anything to apply to.',
  },
  {
    id: 'claim',
    label: 'Claiming',
    blurb: 'What happens the moment a schedule worker sees the shift open — when the first confirm goes, when claiming stops, and when the shift is given up.',
  },
  {
    id: 'after',
    label: 'After the win',
    blurb: 'Finishing the application once the seat is held.',
  },
  {
    id: 'limits',
    label: 'Limits',
    blurb: 'How long a run and a sign-in may live, and how much of each call is kept.',
  },
  {
    id: 'sounds',
    label: 'Sounds',
    blurb:
      'What the console plays when a shift is sighted, a claim fires, a hit is missed, or a shift is won. Uploads for every console reach the whole fleet; voice picks and volume stay this browser’s own. Applied the moment you change them — never part of Update.',
  },
  {
    id: 'live',
    label: 'Live',
    blurb: 'Every hunt this process has in flight.',
  },
] as const

export type TabId = (typeof TABS)[number]['id']

/* ------------------------------------------------------------------ *
 * The draft
 * ------------------------------------------------------------------ */

/**
 * The editable draft, as strings.
 *
 * Strings rather than numbers because a half-typed field is a string: a box
 * being cleared to be retyped is `''`, and holding these as numbers would turn
 * that into a 0 the operator did not type and then render it back at them.
 * Units are the operator's, not the wire's — see `draftFrom`.
 */
export interface Draft {
  market: string
  confirmMarket: string
  workers: string
  gqlWorkers: string
  gqlLogCalls: string
  failedUpdateStreakMax: string
  claimUnavailableMax: string
  seatEmptyReads: string
  rateLimitParkAfter: string
  rateLimitParkMin: string
  claimGapMs: string
  firstActiveHoldSec: string
  huntFlow: string
  brazilUuidMissing: string
  brazilRefererIds: string
  assessmentEnabled: string
  burstWaitSec: string
  burstMaxLoops: string
  burstCreates: string
  burstUpdates: string
  burstGapMs: string
  burstCreateAttempts: string
  burstUnavailableMax: string
  /** `on` | `off`, like every other switch on this page. */
  brazilNoUuid: string
  /** `on` | `off`. The current flow's job and shift UUID form. */
  currentUuid: string
  /** Seconds here, like `burstWaitSec`. Multiplied on save. */
  noUuidWaitSec: string
  noUuidGapMinMs: string
  noUuidGapMaxMs: string
  noUuidUnavailableMax: string
  /** Held in SECONDS here, like `burstWaitSec`. Multiplied on save. */
  burstUnavailableCooldownSec: string
  /** Seconds here too, for the same reason. */
  burstReassignDelaySec: string
  burstSolveBudget: string
  burstLogEvery: string
  burstFleetConcurrency: string
  interval: string
  matchPoll: string
  /** `on` | `off`, like every other switch on this page. */
  repickSeatsGone: string
  /** Minutes past midnight, held as a string like every other field here. */
  dayShiftStart: string
  dayShiftEnd: string
  accounts: string
  rotate403: string
  maxHours: string
  logCalls: string
  nheMin: string
  nheMax: string
  sessionMaxMin: string
  sessionRenewMin: string
  keepAliveSec: string
  watchdogMin: string
  gaWorkers: string
  gaPollMs: string
  gaAccounts: string
  idvLinkGapMs: string
  resumeStaggerMs: string
}

export type FieldKey = keyof Draft

/**
 * Server values → the form's strings.
 *
 * One function rather than the three near-identical literals it replaces (the
 * initial seed, the post-save re-seed, and the dirty check). They have to agree
 * exactly — including the unit conversions, which is where a divergence would
 * hide: a field converted on one path and not the other looks correct until you
 * save, and then the number silently changes scale under you.
 */
export function draftFrom(s: HuntSettingsDto): Draft {
  return {
    market: s.market,
    confirmMarket: s.confirmMarket,
    workers: String(s.workersPerCustomer),
    gqlWorkers: String(s.graphqlWorkersPerCustomer),
    gqlLogCalls: String(s.graphqlWorkerLogCalls),
    failedUpdateStreakMax: String(s.failedUpdateStreakMax),
    claimUnavailableMax: String(s.claimUnavailableMax),
    seatEmptyReads: String(s.seatEmptyReads),
    rateLimitParkAfter: String(s.rateLimitParkAfter),
    rateLimitParkMin: String(s.rateLimitParkMs / 60_000),
    claimGapMs: String(s.claimGapMs),
    firstActiveHoldSec: String(s.firstActiveHoldMs / 1_000),
    huntFlow: s.huntFlow,
    brazilUuidMissing: s.brazilUuidMissing,
    brazilRefererIds: s.brazilRefererIds,
    assessmentEnabled: s.assessmentEnabled ? 'on' : 'off',
    // SECONDS. A minute-scale wait is a seconds-scale decision — the same
    // reasoning the keep-alive period is edited in seconds for.
    burstWaitSec: String(s.burstWaitMs / 1_000),
    burstMaxLoops: String(s.burstMaxLoops),
    burstCreates: String(s.burstCreatesPerLoop),
    burstUpdates: String(s.burstUpdatesPerLoop),
    /*
     * Plain milliseconds, deliberately, where the wait above is seconds.
     *
     * The value that matters here is 0, and dividing it would present "no gap"
     * as "0 seconds" — a rounded number rather than the deliberate setting it
     * is. Same argument `interval` and `idvLinkGapMs` are held in ms for.
     */
    burstGapMs: String(s.burstGapMs),
    burstCreateAttempts: String(s.burstCreateAttempts),
    burstUnavailableMax: String(s.burstUnavailableStreakMax),
    brazilNoUuid: s.brazilNoUuidFlow ? 'on' : 'off',
    currentUuid: s.currentUuidFlow ? 'on' : 'off',
    // Seconds for the wait, milliseconds for the gap, on exactly the split the
    // burst's own fields use: a wait is a tens-of-seconds decision, a gap is read
    // and tuned in milliseconds.
    noUuidWaitSec: String(s.burstNoUuidWaitMs / 1_000),
    noUuidGapMinMs: String(s.burstNoUuidGapMinMs),
    noUuidGapMaxMs: String(s.burstNoUuidGapMaxMs),
    noUuidUnavailableMax: String(s.burstNoUuidUnavailableStreakMax),
    // SECONDS, like the wait above: a cooldown is a minute-scale decision, and 0
    // is still 0 either way, so nothing is lost by rounding here.
    burstUnavailableCooldownSec: String(s.burstUnavailableCooldownMs / 1_000),
    burstReassignDelaySec: String(s.burstReassignDelayMs / 1_000),
    burstSolveBudget: String(s.burstSolveBudget),
    burstLogEvery: String(s.burstLogEvery),
    burstFleetConcurrency: String(s.burstFleetConcurrency),
    interval: String(s.pollIntervalMs),
    // Plain milliseconds, no conversion — an operator tuning a 100ms poll is
    // thinking in milliseconds, and a unit change here is the divergence this
    // function exists to prevent.
    matchPoll: String(s.scheduleMatchPollMs),
    repickSeatsGone: s.repickWhenSeatsGone ? 'on' : 'off',
    dayShiftStart: String(s.dayShiftStartMin),
    dayShiftEnd: String(s.dayShiftEndMin),
    accounts: String(s.sessionAccountsPerWorker),
    rotate403: String(s.accountRotateAfter403),
    maxHours: String(s.maxHuntMs / 3_600_000),
    logCalls: String(s.workerLogCalls),
    nheMin: String(s.nheJitterMinMs / 1_000),
    nheMax: String(s.nheJitterMaxMs / 1_000),
    // Minutes, like the watchdog below: these are hour-scale figures and nobody
    // reasons about a session lifetime in milliseconds.
    sessionMaxMin: String(s.sessionMaxAgeMs / 60_000),
    sessionRenewMin: String(s.sessionRenewMs / 60_000),
    // SECONDS, not minutes like the two above it. A heartbeat is a
    // seconds-scale decision — the anti-bot window it exists to stay inside is
    // about sixty of them — and "1.5 minutes" is not how anybody says 90s.
    keepAliveSec: String(s.keepAliveMs / 1_000),
    watchdogMin: String(s.onboardingWatchdogMs / 60_000),
    gaWorkers: String(s.schedulesWorkersPerCustomer),
    gaPollMs: String(s.schedulesPollMs),
    gaAccounts: String(s.schedulesAccountsPerWorker),
    // Plain milliseconds. The value that matters most here is 0, and dividing
    // it into some larger unit would present "no gap" as "0 seconds" — which
    // reads like a rounded number rather than the deliberate setting it is.
    idvLinkGapMs: String(s.idvLinkGapMs),
    // Milliseconds for the same reason as the gap above: the interesting values
    // are small and 0 is one of them.
    resumeStaggerMs: String(s.resumeStaggerMs),
  }
}

/**
 * The draft → the PUT body.
 *
 * The mirror of `draftFrom`, and it lives beside it for the same reason the
 * seed and the dirty check do: every unit conversion in this file has to appear
 * exactly twice, here and there, and a pair that disagrees is a number that
 * changes scale on save.
 */
export function toPayload(d: Draft): Partial<HuntSettingsDto> {
  return {
    market: d.market,
    confirmMarket: d.confirmMarket,
    workersPerCustomer: Number(d.workers),
    graphqlWorkersPerCustomer: Number(d.gqlWorkers),
    graphqlWorkerLogCalls: Number(d.gqlLogCalls),
    failedUpdateStreakMax: Number(d.failedUpdateStreakMax),
    claimUnavailableMax: Number(d.claimUnavailableMax),
    seatEmptyReads: Number(d.seatEmptyReads),
    rateLimitParkAfter: Number(d.rateLimitParkAfter),
    rateLimitParkMs: Math.round(Number(d.rateLimitParkMin) * 60_000),
    claimGapMs: Number(d.claimGapMs),
    firstActiveHoldMs: Math.round(Number(d.firstActiveHoldSec) * 1_000),
    huntFlow: d.huntFlow,
    brazilUuidMissing: d.brazilUuidMissing,
    brazilNoUuidFlow: d.brazilNoUuid === 'on',
    currentUuidFlow: d.currentUuid === 'on',
    brazilRefererIds: d.brazilRefererIds,
    assessmentEnabled: d.assessmentEnabled === 'on',
    burstWaitMs: Math.round(Number(d.burstWaitSec) * 1_000),
    burstMaxLoops: Number(d.burstMaxLoops),
    burstCreatesPerLoop: Number(d.burstCreates),
    burstUpdatesPerLoop: Number(d.burstUpdates),
    burstGapMs: Number(d.burstGapMs),
    burstCreateAttempts: Number(d.burstCreateAttempts),
    burstUnavailableStreakMax: Number(d.burstUnavailableMax),
    burstNoUuidWaitMs: Math.round(Number(d.noUuidWaitSec) * 1_000),
    burstNoUuidGapMinMs: Number(d.noUuidGapMinMs),
    burstNoUuidGapMaxMs: Number(d.noUuidGapMaxMs),
    burstNoUuidUnavailableStreakMax: Number(d.noUuidUnavailableMax),
    burstUnavailableCooldownMs: Math.round(Number(d.burstUnavailableCooldownSec) * 1_000),
    burstReassignDelayMs: Math.round(Number(d.burstReassignDelaySec) * 1_000),
    burstSolveBudget: Number(d.burstSolveBudget),
    burstLogEvery: Number(d.burstLogEvery),
    burstFleetConcurrency: Number(d.burstFleetConcurrency),
    pollIntervalMs: Number(d.interval),
    scheduleMatchPollMs: Number(d.matchPoll),
    repickWhenSeatsGone: d.repickSeatsGone === 'on',
    dayShiftStartMin: Number(d.dayShiftStart),
    dayShiftEndMin: Number(d.dayShiftEnd),
    sessionAccountsPerWorker: Number(d.accounts),
    accountRotateAfter403: Number(d.rotate403),
    workerLogCalls: Number(d.logCalls),
    maxHuntMs: Math.round(Number(d.maxHours) * 3_600_000),
    nheJitterMinMs: Math.round(Number(d.nheMin) * 1_000),
    nheJitterMaxMs: Math.round(Number(d.nheMax) * 1_000),
    sessionMaxAgeMs: Math.round(Number(d.sessionMaxMin) * 60_000),
    sessionRenewMs: Math.round(Number(d.sessionRenewMin) * 60_000),
    keepAliveMs: Math.round(Number(d.keepAliveSec) * 1_000),
    onboardingWatchdogMs: Math.round(Number(d.watchdogMin) * 60_000),
    schedulesWorkersPerCustomer: Number(d.gaWorkers),
    schedulesPollMs: Number(d.gaPollMs),
    schedulesAccountsPerWorker: Number(d.gaAccounts),
    idvLinkGapMs: Number(d.idvLinkGapMs),
    resumeStaggerMs: Number(d.resumeStaggerMs),
  }
}

/**
 * Are two draft values the same?
 *
 * Numeric where both sides ARE numbers, so typing `08` in a box holding `8` is
 * not a change. String otherwise, because not every field is a number:
 * comparing `market` numerically gives `NaN !== NaN`, which is true forever,
 * and Update would sit permanently lit on a form nobody had touched.
 */
export function same(a: string, b: string): boolean {
  const na = Number(a)
  const nb = Number(b)
  if (a.trim() !== '' && b.trim() !== '' && Number.isFinite(na) && Number.isFinite(nb)) {
    return na === nb
  }
  return a === b
}

/** Every key whose draft value differs from the saved one. */
export function changedKeys(draft: Draft, saved: Draft): FieldKey[] {
  return (Object.keys(FIELD_DEFS) as FieldKey[]).filter((k) => !same(draft[k], saved[k]))
}

/* ------------------------------------------------------------------ *
 * Field metadata
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Which fleet a setting governs
 * ------------------------------------------------------------------ */

/**
 * The hunt runs three independent worker fleets — schedule, GraphQL and GA —
 * and most settings on this page govern exactly one of them.
 *
 * ── Why this had to become a first-class field ──────────────────────
 * "Stop claiming after" and "Gap between claims" bound the SCHEDULE fleet and
 * nothing else — the GraphQL and GA fleets never claim, so neither is held
 * down by either. That was written in the API's source and nowhere on the
 * screen, so two settings that look fleet-wide are not, and an operator tuning
 * them to calm a noisy reading fleet would change nothing at all and have no
 * way to find out why.
 *
 * The same trap is under "Call logs", where two near-identical rows are two
 * different Redis budgets for two different fleets.
 *
 * So every field declares its scope, and the badge is rendered from that rather
 * than left to whichever sentence happened to mention a fleet by name.
 */
export type ScopeKey = 'schedule' | 'graphql' | 'ga' | 'fleets' | 'run'

export interface ScopeMeta {
  /** The fleet's name as the people who run this actually say it. */
  label: string
  /** One line, for the tooltip: what this scope covers. */
  hint: string
}

export const SCOPES: Record<ScopeKey, ScopeMeta> = {
  schedule: {
    label: 'Schedule workers',
    hint: 'Only the schedule fleet — the pollers reading one schedule’s status, and the only fleet that claims. The GraphQL and GA fleets are unaffected.',
  },
  graphql: {
    label: 'GraphQL workers',
    hint: 'Only the GraphQL fleet — the workers asking a job which schedules it is advertising, to read a shift’s UUID off the answer. They never claim.',
  },
  ga: {
    label: 'GA workers',
    hint: 'Only the GA fleet — the workers reading a job’s schedule list BEFORE an application exists, waiting for it to advertise anything at all, and again while a shift has no UUID. They never claim.',
  },
  fleets: {
    label: 'The claim',
    hint: 'The claim itself — what is sent once the schedule workers have seen the shift open — rather than any one reading fleet.',
  },
  run: {
    label: 'Whole run',
    hint: 'Not a fleet setting: this is true of the customer’s whole run, whichever workers are on.',
  },
}

export interface SelectOption {
  value: string
  label: string
}

export interface FieldMeta {
  /** The section this field renders on. Drives the per-tab change count. */
  tab: TabId
  /** Which worker fleet this actually governs. See `SCOPES`. */
  scope: ScopeKey
  /** The card it renders inside. Shown as a breadcrumb in search results. */
  group: string
  /** Short and plain. No wire names, no endpoint names, no jargon. */
  label: string
  /** ONE sentence: what this number does. Always on screen. */
  summary: string
  /** Everything else — the reasoning, the cost, the failure it prevents. */
  detail?: string
  /** Rendered beside the box, so the number is not the only clue to the unit. */
  unit?: string
  /** What 0 means here, when 0 is a posture rather than a quantity. */
  zero?: string
  kind: 'number' | 'select'
  options?: readonly SelectOption[]
  /**
   * How to name a value `options` does not carry, for the one field where the
   * offered set is a convenience rather than the whole range.
   *
   * The day window accepts any minute on the server but offers 48 half hours
   * here, so a value set by hand or by an older build has no option to select.
   * A `<select>` shows its FIRST option for a value it does not have, which
   * would display `12:00 AM` over a stored `5:20 AM` and then save that the next
   * time anything else on the page changed — a setting silently rewritten by
   * opening a form. `Field` appends the real value when this is present, and
   * `displayValue` uses it for the same reason.
   */
  labelFor?: (value: string) => string
  /** Mirrors the API's schema. See the note at the top of this file. */
  min?: number
  /**
   * A floor that depends on another field, for the one setting whose does.
   *
   * `min` above is a constant because every other bound in this file is one.
   * GA workers is not: the Brazil flow arms no GA workers at all, so 0 is a
   * legitimate value there and a refusal everywhere else — and the server
   * decides it the same way, from the flow in the same save. Wins over `min`
   * when present.
   */
  minFor?: (draft: Draft) => number
  max?: number
  /**
   * For the three fields whose rule is "0, OR between X and max".
   *
   * `keepAliveMs`, `sessionRenewMs` and `onboardingWatchdogMs` are validated
   * with a `refine` rather than a plain min/max, because 0 is a first-class
   * posture on each of them and is not the bottom of their range — it is
   * outside it. A form that reported these as "0 – 720" would be telling an
   * operator that 0.5 is fine when the server will refuse it.
   */
  minNonZero?: number
  step?: number
  /** Wire names, endpoint names and error codes, so search finds them too. */
  keywords?: string
  /** Marks a field only one flow reads, so search results are not misleading. */
  only?: string
}

const CONFIRM_OPTIONS: readonly SelectOption[] = [
  { value: 'auto', label: 'Follow the jobs country' },
  ...MARKETS.map((m) => ({ value: m.code, label: `${m.label} — ${m.host}` })),
]

/**
 * Every field, in the order it is read.
 *
 * Insertion order is meaningful: it is the order search results appear in, and
 * it matches the order the cards render, so a result list reads as a shortened
 * version of the page rather than an arbitrary shuffle of it.
 */
export const FIELD_DEFS: Record<FieldKey, FieldMeta> = {
  /* ---- Market ---- */
  market: {
    tab: 'market',
    scope: 'run',
    group: 'Country',
    label: 'Jobs country',
    summary:
      'Which country’s job board this console hunts. Every call after sign-in goes to that country’s site.',
    detail:
      'Signing in is pinned to the US host and does not follow this. The country also sets the locale, the country name sent in request bodies, the JOB-…/SCH-… id prefixes, and which government-id label the background-check form asks for. Sessions belong to the host they were minted on, so switching discards every stored session and the next run for each customer signs in from scratch.',
    kind: 'select',
    options: MARKETS.map((m) => ({ value: m.code, label: m.label })),
    keywords: 'market country canada CA united states US portal host locale',
  },
  confirmMarket: {
    tab: 'market',
    scope: 'run',
    group: 'Country',
    label: 'Where the claim is sent',
    summary:
      'The portal the final confirm call goes to. “Follow the jobs country” is the normal setting.',
    detail:
      'Pinning a country sends the shift confirm — update-application / job-confirm — and its keep-alive beat to that country’s portal, while create, every reading fleet and onboarding stay on the jobs country. The ids in the body never follow: a Canadian hunt keeps sending JOB-CA-… / SCH-CA-… whatever this says. Takes effect on the next run for each customer, not on one already going.',
    kind: 'select',
    options: CONFIRM_OPTIONS,
    keywords: 'confirm endpoint update-application job-confirm host auto override',
  },

  /* ---- Finding a shift: the schedule watch ---- */
  workers: {
    tab: 'find',
    scope: 'schedule',
    group: 'Schedule workers',
    label: 'Schedule workers per customer',
    summary:
      'How many schedule workers read this customer’s schedule at once. More of them means a fresher answer, not a faster one each.',
    detail:
      'They never wait for each other, so five on a one-second cadence means a fresh answer roughly every 200ms while each one still asks only once a second. This is the fleet DEFAULT — a customer on the Approved list can be given their own count. This is the only fleet that watches for the opening and the only one that claims, so 0 leaves nothing watching and a hunt refuses to arm. Each worker also claims its own ISP address for the whole hunt.',
    unit: 'workers',
    zero: 'off',
    kind: 'number',
    min: 0,
    max: 50,
    keywords: 'pollers schedule watch get-schedule-details hunt_workers detector',
  },
  accounts: {
    tab: 'find',
    scope: 'schedule',
    group: 'Schedule workers',
    label: 'Accounts per schedule worker',
    summary:
      'Borrowed accounts one schedule worker cycles through, one per check, so no single identity looks busy.',
    detail:
      'The endpoint limits per CALLER: five workers on one identity is one caller asking twenty-five times a second, while the same five cycling five accounts each is twenty-five callers asking once. Worker count buys coverage; this buys the right to use it. The rows come from a pool shared with the other system, and the budget line below shows what is left. 0 = use the customer’s own session for everything.',
    unit: 'accounts',
    zero: 'own session',
    kind: 'number',
    min: 0,
    max: 25,
    keywords: 'session accounts pool identity ring 429 rate limit',
  },
  rotate403: {
    tab: 'find',
    scope: 'schedule',
    group: 'Schedule workers',
    label: 'Replace an account after',
    summary:
      'Refusals in a row from one borrowed schedule-worker account before it is swapped out. Any success resets the count.',
    detail:
      'A 403 is not proof an account is finished — it is also what one identity gets for asking too often too quickly, and that clears on its own within a poll or two. So 1 (swap on the first refusal) trades a little wasted rate for a lot of churn, and the churn is the expensive half: every swap is three writes against a collection the other system shares, plus a cold identity for the next check. 401 is deliberately not counted; a refused identity stays refused however many times you ask.',
    unit: '× 403',
    kind: 'number',
    min: 1,
    max: 50,
    keywords: 'rotate swap 403 forbidden refused account',
  },
  interval: {
    tab: 'find',
    scope: 'schedule',
    group: 'Schedule workers',
    label: 'Minimum gap between schedule checks',
    summary:
      'The least one schedule worker waits before checking again.',
    detail:
      'A floor, not a cadence — the loop already waits for its own answer, so the real rate is set by the round trip rather than by this. Divide it by the worker count for this customer’s effective detection interval.',
    unit: 'ms',
    zero: 'no gap',
    kind: 'number',
    min: 0,
    max: 60_000,
    step: 50,
    keywords: 'poll interval gap cadence rate',
  },
  matchPoll: {
    tab: 'find',
    scope: 'run',
    group: 'Picking a shift to hunt',
    label: 'Look for a matching shift every',
    summary:
      'For a customer with no job and no shift yet: how often the schedule list is re-read for one that fits their sites and hours.',
    detail:
      'One query for the whole API per tick, not one per waiting customer — the snapshot is shared and every parked customer is evaluated against it in memory, which is what makes 100ms affordable here. The floor is 25ms rather than the 0 the gap above allows, because this drives a real timer instead of a loop that waits for its own answer, and 0 there is a spin rather than a cadence.',
    unit: 'ms',
    kind: 'number',
    min: 25,
    max: 60_000,
    step: 25,
    keywords: 'schedule match parked waiting no job auto pick',
  },
  repickSeatsGone: {
    tab: 'find',
    scope: 'run',
    group: 'Picking a shift to hunt',
    label: 'Give up a shift once it fills',
    summary:
      'Brazil flow only: whether an automatically picked shift is given up at the end of a burst when the schedule workers have read it with no free seat.',
    detail:
      'A shift is only ever picked automatically when the schedule list says it has room. After the pick that count is no longer polled from the database — it is whatever the schedule workers last read off the schedule itself, which is the number beside the AUTO chip on the sessions list. Turned on, a Brazil burst that ends with that reading at zero sends the customer back to the picker for another shift. Turned off, the number is shown and nothing acts on it: the customer keeps hunting a queue that is already full. Applies ONLY to an automatic pick — a customer with a job and shift set on their row is never re-assigned — and never interrupts a burst in progress. The shift given up is NOT excluded from the search; if its count rises again it is an ordinary candidate like any other. The wait before the next pick is the same one the burst uses on a refused seat. The Current flow ignores this and gives a shift up on the two settings under “When to give the shift up” on the Claim tab.',
    kind: 'select',
    options: [
      { value: 'off', label: 'Off — keep hunting the shift that filled' },
      { value: 'on', label: 'On — pick another shift' },
    ],
    only: 'brazil',
    keywords: 'seats labour labor headcount full zero repick re-pick reassign auto pick another burst',
  },
  dayShiftStart: {
    tab: 'find',
    scope: 'run',
    group: 'Picking a shift to hunt',
    label: 'Day shifts start at',
    summary:
      'A full-time shift starting at or after this — and before the end below — counts as a day shift. Everything else counts as night.',
    detail:
      'Only used when a job and shift are picked automatically, and only for FULL-TIME shifts: it is matched against the customer’s own Day / Night / Any preference. Part-time and flex customers are never filtered on it, and a customer set to Any ignores it entirely. A shift is judged by the time it STARTS, never by when it ends — a 1:20 a.m. to 11:50 a.m. shift is a night shift under the 5:00 AM default, however much daylight it covers, and that is worth knowing because it is where a large part of the shift list sits. Night is whatever this window leaves over, so no shift can fall in both halves or in neither. Setting the start later than the end is allowed and wraps through midnight. A customer already waiting picks up a change within a couple of seconds, without a restart.',
    kind: 'select',
    options: CLOCK_OPTIONS,
    labelFor: (v) => clockLabel(Number(v)),
    keywords: 'day night shift priority preference hours am pm schedule text full-time',
  },
  dayShiftEnd: {
    tab: 'find',
    scope: 'run',
    group: 'Picking a shift to hunt',
    label: 'Day shifts end at',
    summary:
      'The other end of the day window. A full-time shift starting at or after this is a night shift.',
    detail:
      'The window is half-open: a shift starting exactly at the start time above is a day shift, and one starting exactly at this time is a night shift. This may not be the same as the start — night is defined as whatever the day window leaves over, so a window of zero length would leave nothing to be day and the save is refused.',
    kind: 'select',
    options: CLOCK_OPTIONS,
    labelFor: (v) => clockLabel(Number(v)),
    keywords: 'day night shift priority preference hours am pm schedule text full-time',
  },

  /* ---- Finding a shift: the shift list ---- */
  gqlWorkers: {
    tab: 'find',
    scope: 'graphql',
    group: 'GraphQL workers',
    label: 'GraphQL workers per customer',
    summary:
      'On the Current flow, GraphQL workers run only while the assigned shift has no UUID: they read the job’s shift list to find it, then stand down. They never claim.',
    detail:
      'The shift list answers with every schedule the job is advertising, each carrying its numeric id AND its UUID — so the moment the run’s own shift is listed, a worker matches the id, takes the UUID beside it, and every confirm from then on names the shift by it. That is the whole of this fleet’s work on the Current flow: once the UUID is on file these workers hand their ISP addresses back, and a run whose shift already has one never arms them at all. This count sizes that fleet and is floored at 1 on the server, so the fleet that reads the UUID always exists; the GA workers read the same listing on a different endpoint, and whichever sees it first supplies it. Each worker holds its own ISP address and no accounts. Overridable per customer, exactly as the watcher count is.',
    unit: 'workers',
    zero: 'raised to 1',
    kind: 'number',
    min: 0,
    max: 50,
    keywords: 'graphql searchScheduleCards shift list hunt_gql_workers uuid scheduleUuid stand down',
  },

  gaWorkers: {
    tab: 'find',
    scope: 'ga',
    group: 'GA workers',
    label: 'GA workers per customer',
    summary:
      'GA workers read a job’s schedule list while the assigned shift has no UUID, to find the shift and take the UUID beside it.',
    detail:
      'On the Current flow this fleet arms alongside the GraphQL workers only while the assigned shift has no UUID: it reads the job’s schedule list to find the shift and take the UUID beside it, then stands down. It never claims. It cannot be turned off there — a shift with no UUID needs at least one reader — so the count is floored at 1. The Brazil flow does not run this fleet for an ordinary run: it hunts in claim mode with its own shift-list claimers, so 0 is simply what is already true there and the box accepts it — except for a run on a shift with no UUID, which arms these workers to read the UUID whatever this says. It used to run BEFORE an application existed too, waiting for the job to advertise a shift so one could be created; the create now goes out at Start and is retried if refused, with no worker armed until it takes. It is authenticated, so each worker holds its own ISP address AND a ring of borrowed accounts while it runs. That is why the ceiling is far below the schedule fleet’s.',
    unit: 'workers',
    zero: 'off — only the Brazil flow may',
    kind: 'number',
    min: 1,
    // Mirrors `schedulesWorkersFloor` on the server, which reads the flow out of
    // the same save. Switching the flow back to Current raises a stored 0 to 1
    // on that write, so the two can disagree only for as long as the form is open.
    minFor: (d) => (d.huntFlow === 'brazil' ? 0 : 1),
    max: 25,
    keywords: 'get-all-schedules availableSchedules total schedule_workers uuid spares GA off zero',
  },
  gaAccounts: {
    tab: 'find',
    scope: 'ga',
    group: 'GA workers',
    label: 'Accounts per GA worker',
    summary:
      'Borrowed identities each GA worker cycles, one per read. 0 falls back to the customer’s own token.',
    detail:
      'This endpoint asks about a JOB and never about a candidate, so any valid session answers it — which is what makes borrowing legitimate here rather than a trick. Each account’s rate is the worker’s rate divided by this, so raising it spreads the load without raising the call rate. It draws on the session-account pool, which is the tighter of the two shared pools, for as long as the fleet runs.',
    unit: 'accounts',
    zero: 'own token only',
    kind: 'number',
    min: 0,
    max: 25,
    keywords: 'session accounts borrowed identity rotation GA get-all-schedules',
  },
  gaPollMs: {
    tab: 'find',
    scope: 'ga',
    group: 'GA workers',
    label: 'Gap between GA reads',
    summary:
      'The minimum a GA worker waits between reads. 0 reads again the moment the last answer lands.',
    detail:
      'Gapless by default, like the schedule watch and the shift list: a shift can be advertised and gone inside a poll interval, and detection lag is the only latency here anybody can do anything about. What it costs is real — every read is a request from a leased address on a borrowed identity, and a 403 benches that address for 45 seconds for this system and the one that shares the pool. Raise this the moment 403s, rotations or proxy spend climb; it reaches the fleets already running rather than only the next customer.',
    unit: 'ms',
    zero: 'no gap',
    kind: 'number',
    min: 0,
    max: 600_000,
    keywords: 'gap interval poll GA get-all-schedules pace gapless',
  },

  /* ---- After the win: the recorded assessment ---- */
  assessmentEnabled: {
    tab: 'after',
    scope: 'run',
    group: 'Assessment',
    label: 'Sit the assessment',
    summary:
      'Whether the application finishes Amazon’s recorded assessment, or stops and waits for a person.',
    detail:
      'Amazon asks for a recorded assessment before the application can be submitted: a few instructional pages, a work-history questionnaire, two timed exercises, and five spoken questions answered by recording audio. Turned on, the wizard sits it inline — about two minutes. Turned off, the run ends at that step and says a person is needed, WITHOUT telling Amazon it was done. Two other switches also have to be on: the deployment’s (WS_STOP_ON_ASSESSMENT_CONSENT) and the customer’s own (assessment_enabled on their row), which is how the first live runs are done one customer at a time. A customer with no Gender on file is refused by name — there is deliberately no default voice. Changing this does not interrupt an assessment already running.',
    kind: 'select',
    options: [
      { value: 'off', label: 'Off — stop and wait for a person' },
      { value: 'on', label: 'On — sit it automatically' },
    ],
    keywords: 'assessment audio voice recorded lpaca spoken elevenlabs openai',
  },

  /* ---- Claiming: the shape ---- */
  huntFlow: {
    tab: 'claim',
    scope: 'fleets',
    group: 'How the claim is made',
    label: 'Claim flow',
    summary:
      'Current fires one confirm the instant a shift is seen open. Brazil waits out the early-warning window first, then bursts.',
    detail:
      'A schedule reports ACTIVE roughly 60–240 seconds before the seat can actually be granted, which is what the Brazil burst exists to sit through. Brazil also changes what START does: it files no application until a shift opens, because on the v1/workflow endpoint creating the application and being granted the seat are the same call. Hunts already running keep the flow they armed with, so this reaches a customer on their next Start.',
    kind: 'select',
    /*
     * The two flows keep their NAMES, not just their descriptions.
     *
     * "Current" and "Brazil" are what the commit history, the run logs and
     * everyone who works on this call them. Relabelling the options to plain
     * descriptions read better in isolation and made the page worse: somebody
     * told "put that customer on Brazil" then had to work out which of two
     * unnamed descriptions that was. The name leads, the sentence follows it.
     */
    options: [
      { value: 'current', label: 'Current — one confirm per sighting' },
      { value: 'brazil', label: 'Brazil — burst after a wait' },
    ],
    keywords: 'flow brazil current v1 workflow burst strategy',
  },
  brazilUuidMissing: {
    tab: 'claim',
    /*
     * `run`, not `fleets`. It does not tune a worker — it decides whether the
     * customer applies at all, which is true of the whole run whichever
     * detectors are on.
     */
    scope: 'run',
    group: 'How the claim is made',
    label: 'When a shift has no UUID',
    summary:
      'Blocking is the default: sending the old numeric ids instead produces a run that looks healthy and never wins.',
    detail:
      'The Brazil flow addresses a shift by a UUID that lives in the schedule collection, and the schedule poller fills those in on its own slow cadence — most shifts do not have one at any given moment, and the ones that do took between about 10 minutes and an hour and a half to get there. Blocking stops the run and says which shift and why. Falling back sends the numeric ids, which is exactly what this system did before UUIDs existed; those calls do not error, they simply stop winning, so use it while proving the new form against the live service and not after. Parking holds the session and the ISP address and waits for the UUID to appear — it is only ever offered for a shift the collection actually knows about, because waiting on one it has never heard of is a wait that cannot end. This governs every flow, not just Brazil: a shift with no UUID is also one the automatic matcher will not pick.',
    kind: 'select',
    options: [
      { value: 'block', label: 'Block — stop and say the shift has no UUID' },
      { value: 'numeric', label: 'Fall back — send the old numeric ids' },
      { value: 'park', label: 'Park — hold the session and wait for the UUID' },
    ],
    keywords: 'uuid guid scheduleUuid jobUuid block fallback park wait unresolved schedule_checker',
  },
  brazilNoUuid: {
    tab: 'claim',
    // `run`, like the setting above it: it decides which shifts a customer may
    // be put on and which requests are made for them, not how fast a worker goes.
    scope: 'run',
    group: 'How the claim is made',
    label: 'Hunt shifts with no UUID yet',
    summary:
      'Whether a shift the matcher picks for a Brazil customer may be one with no shift UUID, hunted on the job alone until the UUID appears.',
    detail:
      'For a Brazil customer the matcher refuses a shift it cannot address, and that has meant BOTH UUIDs. The job UUID is on every shift; the shift UUID is on about one in five, because the schedule poller fills those in on its own slow cadence — so that one condition hides roughly four fifths of the shifts that have a free seat at any moment. Turned on, a shift with a job UUID and no shift UUID becomes pickable, and the run that gets one works the endpoint differently: it creates an application naming the job and no shift at all, waits out the shorter window below, then sends one confirm at a time on a jittered gap instead of a create and two confirms per pass. Meanwhile the run arms GraphQL and GA workers — the Finding tab’s counts, at least one of each — which read every schedule the job lists; the moment one of them finds the run’s shift, it takes the UUID beside it and the run switches to the ordinary burst mid-opening, keeping the application it already made. Shifts that already have a UUID are always preferred — one of these is picked only when no addressable shift fits the customer at all. Applies ONLY to an automatic pick and only on the Brazil flow: a customer with a job and shift set on their row is still governed by the setting above. Current-flow customers are not affected either way: that flow picks shifts with or without a shift UUID as equals, whatever this is set to. Two of the requests it sends have not been observed against the live service — a create with no shift, and a confirm naming the job by UUID — so prove it on one customer before turning it on for a fleet.',
    kind: 'select',
    options: [
      { value: 'off', label: 'Off — only shifts that already have a UUID' },
      { value: 'on', label: 'On — hunt them on the job alone' },
    ],
    keywords:
      'uuid guid scheduleUuid jobUuid job-only no uuid unresolved pick widen inventory schedule_checker',
  },
  currentUuid: {
    tab: 'claim',
    // `run`, like its two neighbours above: it decides which requests are made
    // for a customer, not how fast a worker goes.
    scope: 'run',
    group: 'How the claim is made',
    label: 'Address the shift by UUID',
    summary:
      'Whether the Current flow names the job by UUID on the create, and the shift by UUID on its confirms.',
    detail:
      'Amazon accepts a job and a shift under two names: the number this system has always sent (JOB-CA-0000000581) and a UUID (JOB-CA-4f825db4-…). Turned on, a Current-flow run creates its application naming the job by UUID — the create has never named a shift at all — while every confirm after it keeps naming the job by its number. The SHIFT half is separate and starts as a number: the schedule poller fills shift UUIDs in between about 10 minutes and an hour and a half after a shift appears, so a run that waited for one would spend most of every opening not hunting. Instead the run confirms against the numeric shift and upgrades the moment one of its own shift-list or GA workers reads the UUID off a live listing, which both of them return beside every schedule they name. From then on every confirm and every keep-alive beat names the shift by UUID too. A run whose JOB has no UUID stops instead of guessing, and says so: every row in the schedule collection carries one, so a pair without it is stale or wrong rather than waiting to resolve — reassign the customer, or turn this off. Turned off, every call is byte for byte what it was before this setting existed. Nothing here touches the Brazil flow, which has its own two settings above.',
    kind: 'select',
    options: [
      { value: 'off', label: 'Off — send the old numeric ids' },
      { value: 'on', label: 'On — job UUID on the create, shift UUID on confirms' },
    ],
    keywords:
      'uuid guid jobUuid scheduleUuid current flow create confirm job-confirm numeric ids upgrade listing',
  },
  brazilRefererIds: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Referer id form',
    summary:
      'Whether the page address sent with each call names the same ids the request does, or keeps the old numeric ones.',
    detail:
      'Every call carries a Referer — the “which page did this come from” line a browser sends automatically — and this system builds a fake one that looks like the real shift page, complete with the job and shift in its query string. Matching the request is what a browser actually does, since the page and the call are about the same shift, and it is the default. Keeping the numeric ids puts the old pair in that address while the request itself carries UUIDs; it exists because there is no capture of Amazon’s UUID-era page to say which form its own address uses, and a header is a cheap thing to be able to change without a deploy. Either way the request body is untouched.',
    kind: 'select',
    options: [
      { value: 'body', label: 'Match the request' },
      { value: 'numeric', label: 'Keep the old numeric ids' },
    ],
    only: 'brazil',
    keywords: 'referer header url page address jobId scheduleId uuid brazil v1 workflow',
  },
  burstWaitSec: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Wait after the shift goes ACTIVE',
    summary: 'How long the burst holds off before its first call. The strategy, not a safety margin.',
    detail:
      'A claim fired at the first sighting is spent into a window that cannot grant it yet. The detectors keep polling at full rate throughout, and a schedule that flickers closed during the wait does NOT cancel it — flicker is expected before the real opening, and standing down on one would forfeit the very opening the wait exists to catch. 0 bursts immediately, which is useful for testing and rarely right on a live shift.',
    unit: 's',
    zero: 'burst at once',
    kind: 'number',
    min: 0,
    max: 600,
    step: 5,
    only: 'brazil',
    keywords: 'active early warning window wait delay',
  },
  burstCreates: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Create calls per pass',
    summary:
      'createApplication calls in each pass. On this endpoint a create can itself be granted the seat.',
    unit: '× create',
    kind: 'number',
    min: 0,
    max: 20,
    only: 'brazil',
    keywords: 'create application v1 workflow',
  },
  burstUpdates: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Confirm calls per pass',
    summary: 'job-confirm calls sent after the creates in each pass.',
    detail:
      'Creates and confirms cannot both be 0 — that is a pass which sends nothing at all. One confirm is forced on save if they are.',
    unit: '× confirm',
    kind: 'number',
    min: 0,
    max: 20,
    only: 'brazil',
    keywords: 'confirm update-application job-confirm',
  },
  burstMaxLoops: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Passes per burst',
    summary:
      'How many passes one burst may run before it gives up on the opening and goes back to watching.',
    detail:
      'Bounds CALLS, not time — the burst carries its own wall clock as well. There is deliberately no “unlimited” here: asking forever against a WAF-guarded endpoint from one pinned address is how an address is retired, not how a shift is won.',
    unit: '× pass',
    kind: 'number',
    min: 1,
    max: 5_000,
    step: 10,
    only: 'brazil',
    keywords: 'iterations loops passes bound',
  },
  burstGapMs: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Pause between passes',
    summary: 'How long the loop waits before starting the next pass.',
    detail:
      'Every call is awaited, so one customer already has exactly one request in flight and the cadence is the endpoint’s own latency. The dial is here for when that proves to be more than a single address can sustain.',
    unit: 'ms',
    zero: 'back-to-back',
    kind: 'number',
    min: 0,
    max: 10_000,
    step: 50,
    only: 'brazil',
    keywords: 'gap pause pacing',
  },
  burstCreateAttempts: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Tries to get an application',
    summary:
      'Before the wait, the burst needs an application. It reuses a stored one when there is one; this bounds how many creates it will spend getting one.',
    detail:
      'Separate from the pass count because it bounds a different thing and runs BEFORE the wait — its job is to have an application in hand by the time the window opens. It has to be able to fail rather than retry forever: it is the only bound on a create the service keeps refusing for a reason this flow does not classify.',
    unit: '× try',
    kind: 'number',
    min: 1,
    max: 200,
    only: 'brazil',
    keywords: 'applicationId bootstrap create attempts',
  },
  burstUnavailableMax: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Stop after “seat not available”',
    summary:
      'SELECTED_SCHEDULE_NOT_AVAILABLE replies in a row that end a burst. 1 stops at the first, which is the default.',
    detail:
      'Once the seat has refused a confirm, the rest of that opening is answered the same way — hundreds of requests from one pinned address, for an answer already given. Standing down costs the opening and keeps the address. Counted consecutively and reset by any other outcome, so higher values give the old keep-asking behaviour back by degrees. 0 keeps confirming straight through it.',
    unit: '× refused',
    zero: 'never stop',
    kind: 'number',
    min: 0,
    max: 1_000,
    only: 'brazil',
    keywords: 'SELECTED_SCHEDULE_NOT_AVAILABLE refused seat stand down',
  },
  noUuidWaitSec: {
    tab: 'claim',
    scope: 'fleets',
    group: 'Shifts with no UUID',
    label: 'Wait before the first confirm',
    summary:
      'How long a run hunting a shift on the job alone waits after ACTIVE before it starts confirming.',
    detail:
      'Its own dial rather than the burst wait above, because the two are bought with different money. That one spends a create on every pass to reach the part of the window where grants happen, so arriving early is expensive. This sequence sends no creates at all once it starts, so arriving early costs almost nothing and arriving late costs the opening. The window runs roughly 60 to 240 seconds, so 30 puts the run in front of the seat for most of it.',
    unit: 's',
    zero: 'confirm immediately',
    kind: 'number',
    min: 0,
    max: 600,
    only: 'brazil',
    keywords: 'uuid job-only wait active window delay',
  },
  noUuidGapMinMs: {
    tab: 'claim',
    scope: 'fleets',
    group: 'Shifts with no UUID',
    label: 'Gap between confirms, at least',
    summary:
      'Before each confirm, a run hunting on the job alone pauses for a random time between this and the figure below.',
    detail:
      'A range rather than one figure. This sequence sends a single small call a pass for the length of an opening, all from the customer’s one pinned address, so a fixed pause is a metronome — the same rate, to the millisecond, from the same address, for minutes. Each gap is drawn fresh, anywhere in the range. The burst gap above ships at 0 because that sequence already paces itself on three awaited calls a pass. At 100–200ms one run makes about five to ten calls a second. Set both ends to the same figure for an exact gap.',
    unit: 'ms',
    zero: 'back-to-back',
    kind: 'number',
    min: 0,
    max: 10_000,
    step: 10,
    only: 'brazil',
    keywords: 'uuid job-only gap pause cadence rate jitter random range minimum confirm',
  },
  noUuidGapMaxMs: {
    tab: 'claim',
    scope: 'fleets',
    group: 'Shifts with no UUID',
    label: '…and at most',
    summary: 'The top of that range. Every gap lands somewhere between the two.',
    detail:
      'The server keeps the maximum at or above the minimum, so the two can be typed in either order without a moment where the range is inverted.',
    unit: 'ms',
    kind: 'number',
    min: 0,
    max: 10_000,
    step: 10,
    only: 'brazil',
    keywords: 'uuid job-only gap pause cadence rate jitter random range maximum confirm',
  },
  noUuidUnavailableMax: {
    tab: 'claim',
    scope: 'fleets',
    group: 'Shifts with no UUID',
    label: 'Stop after “seat not available”',
    summary:
      'Refusals in a row that end a run hunting on the job alone. 1 — the default and the lowest you can set — stops at the first.',
    detail:
      'There is deliberately no “never” here, unlike the same setting for the ordinary burst above. That sequence also sends creates, which break the streak and can themselves be granted the seat, so asking through a refusal can still end in a win. This one sends nothing but confirms, so never standing down is an unbounded run of identical refused calls from that customer’s single pinned address that nothing in the loop can change — which is exactly what was watched happening, thirty-seven and then forty-three times against one shift. Standing down costs the opening and nothing more: the run KEEPS the shift and goes back to waiting for it to go ACTIVE again. Whether to give the shift up is decided by its seat count instead, as the schedule workers last read it — with “Give up a shift once it fills” on, a burst ending on a zero sends the customer back to the picker, and otherwise they keep hunting the shift they have.',
    unit: '× refused',
    kind: 'number',
    min: 1,
    max: 5_000,
    only: 'brazil',
    keywords: 'uuid job-only SELECTED_SCHEDULE_NOT_AVAILABLE refused stand down',
  },
  burstUnavailableCooldownSec: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Cool-off after a refused seat',
    summary: 'How long before this customer may burst again, once a burst stopped on a refused seat.',
    detail:
      'A burst is already one-per-opening, but that is no bound at all on a schedule that closes and reopens within seconds — the latch clears and the customer is back in a full wait against a seat that has just said no. Both must pass: a new opening AND this clock.',
    unit: 's',
    zero: 'off',
    kind: 'number',
    min: 0,
    max: 3_600,
    step: 10,
    only: 'brazil',
    keywords: 'cooldown cool off refused seat latch flap',
  },
  burstReassignDelaySec: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Wait before looking for another shift',
    summary:
      'How long after losing an automatically picked shift before this customer starts looking for a different one. Covers both ways of losing it.',
    detail:
      'A shift picked automatically is lost in more than one way, and this is the pause before the search restarts after any of them. On the Brazil flow a burst loses it outright — the seat refuses the confirm, or the job reports no schedules left — or it quietly fills up and a burst ends with the schedule workers reading no free seat, which is “Give up a shift once it fills” on the Finding tab. On the Current flow the hunt gives it up on the two settings under “When to give the shift up”. Either way the customer stops watching that shift and goes back to the picker for another. Applies ONLY to an automatic pick: a customer with a job and shift set on their row is never re-assigned, and keeps watching the shift they were given. 0 looks again on the very next check, which is also when the same shift is most likely to be picked a second time — the schedule list often still shows seats on a shift the claim has just refused. Raise this to slow that cycle down.',
    unit: 's',
    zero: 'immediately',
    kind: 'number',
    min: 0,
    max: 600,
    step: 5,
    keywords: 'reassign re-assign lost shift auto pick another schedule delay wait again seats full',
  },
  burstSolveBudget: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Bot-check solves per burst',
    summary: 'The only setting here that costs money. Each call may buy one solve, and a burst makes hundreds.',
    detail:
      'A healthy burst buys none — the token is topped up from the mint shelf during the wait and lives minutes. Reaching the budget means the identity or the address is being refused, and four hundred more solves would buy exactly the same answer. 0 removes the limit.',
    unit: '× solves',
    zero: 'no limit',
    kind: 'number',
    min: 0,
    max: 500,
    only: 'brazil',
    keywords: 'capsolver waf challenge captcha cost money spend',
  },
  burstLogEvery: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Log one call in every',
    summary: 'A burst is hundreds of near-identical calls, and the Claim tab holds a few dozen rows.',
    detail:
      'Thinning keeps the shape rather than the tail: the first call, a periodic pulse, every new error code, and the last one. 0 files every call, which is right for a three-pass test and wrong for a live burst.',
    unit: '× calls',
    zero: 'log all',
    kind: 'number',
    min: 0,
    max: 1_000,
    only: 'brazil',
    keywords: 'log thinning sampling claim tab',
  },
  burstFleetConcurrency: {
    tab: 'claim',
    scope: 'fleets',
    group: 'The burst',
    label: 'Burst calls in flight, fleet-wide',
    summary: 'Across ALL customers, not one. Over this, calls wait a few milliseconds instead of all leaving together.',
    detail:
      'A burst paces itself — one awaited call at a time — so the number of requests leaving this container is set by how many customers happen to want the same shift, not by anything a single burst chose. The queue is what stops them all failing at once when the container runs out of capacity to start work.',
    unit: 'at once',
    kind: 'number',
    min: 1,
    max: 2_000,
    only: 'brazil',
    keywords: 'concurrency fleet in flight queue',
  },

  /* ---- Claiming: when to start ---- */
  firstActiveHoldSec: {
    tab: 'claim',
    scope: 'schedule',
    group: 'When to start claiming',
    label: 'Hold after the first ACTIVE',
    summary:
      'The first ACTIVE a schedule worker sees fires one confirm at once. This is how long further confirms are withheld after that first sighting; then every ACTIVE claims.',
    detail:
      'A schedule reports ACTIVE roughly 60–240 seconds before the seat can actually be granted. The first sighting still sends exactly one confirm — cheap, and occasionally the window is already open — and the hold then withholds every further schedule-worker confirm for this many seconds, measured from that first sighting. Once per hunt: the clock starts at the first ACTIVE, and a shift that closes, flickers or reopens does not restart it — only a new hunt, after a restart or a re-pick, does. Schedule workers keep reading at full rate throughout, and the keep-alive beat is not held. GraphQL and GA workers never claim, so there is nothing of theirs to hold. The Brazil flow ignores this and waits on its own “Wait after the shift goes ACTIVE” instead.',
    unit: 's',
    zero: 'claim at once',
    kind: 'number',
    min: 0,
    max: 600,
    step: 5,
    only: 'current',
    keywords: 'hold wait delay first active early warning window job-confirm update-application',
  },

  /* ---- Claiming: when to stop ---- */
  failedUpdateStreakMax: {
    tab: 'claim',
    scope: 'schedule',
    group: 'When to stop claiming',
    label: 'Stop claiming after',
    summary:
      'Refusals in a row — HTTP 429, 403 or 502 — before schedule workers stop claiming. They keep reading at full rate.',
    detail:
      'Only those three count: rate-limited (429), refused at the edge (403, a bot-check block included) and bad gateway (502). FAILED_UPDATE_APPLICATION is ignored — it neither counts nor resets the count — and so are timeouts and other HTTP errors. Not a retry count — one sighting fires exactly one claim. This bounds the opposite shape: a schedule reporting ACTIVE for tens of seconds while the edge refuses everything, where every read is a genuinely new sighting and therefore a new claim. Claiming resumes the moment the schedule reports a closed status, or a claim is answered with any other error code. 0 = never stop.',
    unit: '× refused',
    zero: 'never stop',
    kind: 'number',
    min: 0,
    max: 500,
    step: 5,
    keywords: '429 403 502 rate limit refused bad gateway FAILED_UPDATE_APPLICATION streak stand down cap',
  },
  claimGapMs: {
    tab: 'claim',
    scope: 'schedule',
    group: 'When to stop claiming',
    label: 'Gap between claims',
    summary:
      'The least time between two schedule-worker claims on the same open shift. The first claim on an opening is never delayed by this.',
    detail:
      'The other half of the bound above: the cap says how many refusals are tolerated before we give up on an opening, this says how fast we may ask while we have not. Exact, not jittered — two claims on one opening are one hunt asking twice, not a herd. Cleared the moment a read comes back not-open, so a shift that closes and reopens is claimed instantly again.',
    unit: 'ms',
    zero: 'no gap',
    kind: 'number',
    min: 0,
    max: 10_000,
    step: 50,
    keywords: 'gap claim spacing update-application',
  },

  /* ---- Claiming: when Amazon rate-limits the claims ---- */
  rateLimitParkAfter: {
    tab: 'claim',
    scope: 'run',
    group: 'When Amazon rate-limits the claims',
    label: 'Park the customer after',
    summary:
      'HTTP 429 replies in a row to schedule-worker claims before the customer is parked: no claims, no schedule reads and no heartbeat until the park ends.',
    detail:
      'A 429 is Amazon throttling the account, and every further claim only keeps it going. Each 429 on a claim adds one; any other answer to a claim — another status, or an application code such as FAILED_UPDATE_APPLICATION or SELECTED_SCHEDULE_NOT_AVAILABLE — starts the count again. A reply that says nothing about the throttle leaves it where it is: a bot-check challenge, a 401, 405 or 408, a timeout. The shift closing and reopening does NOT reset it, which is the difference from “Stop claiming after” above — the openings flip between reads on exactly the shifts that draw the throttle. Counted per hunt; each miss line shows how far it has got. At the count the hunt and the heartbeat end, keeping the session and the address, and the customer waits for “Park for”. Then the customer’s row decides: a job and shift set on it are applied to again, otherwise the picker chooses a new shift at random. Current flow only. 0 = never park.',
    unit: '× 429 in a row',
    zero: 'never',
    kind: 'number',
    min: 0,
    max: 500,
    step: 1,
    only: 'current',
    keywords: '429 rate limit rate-limited throttle park pause wait cool down resume re-pick auto schedule update-application',
  },
  rateLimitParkMin: {
    tab: 'claim',
    scope: 'run',
    group: 'When Amazon rate-limits the claims',
    label: 'Park for',
    summary:
      'How long a parked customer waits before applying again — to the job and shift set on them, or to a new shift from the picker.',
    detail:
      'Nothing is sent to Amazon for this long, with one exception: if the sign-in reaches its renewal age during the park, the customer is signed in again (which can send them a code), so they can resume. The sibling system assigning or clearing the job and shift during the park does not end it early — it only changes what the park ends in. A redeploy ends a park like any other wait, and a customer started again after one starts at once. Current flow only.',
    unit: 'min',
    kind: 'number',
    min: 1,
    max: 120,
    step: 1,
    only: 'current',
    keywords: '429 rate limit park duration minutes wait cool down resume',
  },

  /* ---- Claiming: when to give the shift up ---- */
  claimUnavailableMax: {
    tab: 'claim',
    scope: 'schedule',
    group: 'When to give the shift up',
    label: 'Stop claiming after “not available”',
    summary:
      'SELECTED_SCHEDULE_NOT_AVAILABLE replies before the schedule workers stop confirming. An auto-picked shift is then given up; an assigned pair only waits for the shift to open again.',
    detail:
      'Every “not available” the seat sends back to a schedule-worker confirm adds one; the FAILED_UPDATE_APPLICATION replies in between neither count nor reset it. The reply that reaches this number is the one miss the hunt hears and plays the miss sound; the ones before it are not misses, because the next claim is already on its way. What happens then depends on who chose the shift. An auto-picked shift is counted per HUNT — a shift that closes and reopens gets no fresh allowance — and at the cap it is given up: the customer goes back to the picker for another shift. A pair set on the customer (job and shift on the row) is never given up: it is counted per OPENING, at the cap the confirms simply stop, and they start again when the schedule reads closed and then ACTIVE. Current flow only — the Brazil burst has its own bound. 0 = never stop: claim on every sighting, still with one miss sound per opening.',
    unit: '× not available',
    zero: 'never',
    kind: 'number',
    min: 0,
    max: 1000,
    step: 5,
    only: 'current',
    keywords:
      'SELECTED_SCHEDULE_NOT_AVAILABLE seat not available give up re-pick pause wait open again assigned cap stand down miss sound job-confirm',
  },
  seatEmptyReads: {
    tab: 'claim',
    scope: 'schedule',
    group: 'When to give the shift up',
    label: 'Give an auto-picked shift up after empty reads',
    summary:
      'Consecutive schedule-worker reads reporting no free seat (laborDemandAvailableCount below 1) before an auto-picked shift is given up and the customer looks for another. An assigned pair is not checked.',
    detail:
      'Counted across ALL schedule workers of the hunt, so five workers reach 30 in about six seconds of gapless reads. Any read that reports a free seat resets the run; a read that does not parse as a schedule, or carries no count, moves it neither way. Only an auto-picked shift is judged by it. A pair set on the customer (job and shift on the row) is never given up on this rule: the count is shown on the hunt drawer and nothing acts on it. Current flow only. 0 = never give the shift up on this rule.',
    unit: 'reads',
    zero: 'off',
    kind: 'number',
    min: 0,
    max: 1000,
    step: 5,
    only: 'current',
    keywords: 'laborDemandAvailableCount seats empty full give up re-pick shift gone auto-picked assigned not checked',
  },

  /* ---- Claiming: identity verification ---- */
  idvLinkGapMs: {
    tab: 'claim',
    scope: 'run',
    group: 'Identity verification',
    label: 'Gap between ID-check link tries',
    summary:
      'Only runs for a customer Amazon refuses to create an application for until they have proved who they are.',
    detail:
      'The poll keeps asking for a verification link until one comes back, then hands it to Telegram. Jittered by up to 10% so the fleet’s blocked customers do not all land on the same tick. It does NOT change the 60-second clock the same poll retries the APPLICATION on — that one waits on a human finishing a KYC flow and can buy a solve every time it fires. 0 = ask again the moment the last answer lands.',
    unit: 'ms',
    zero: 'no gap',
    kind: 'number',
    min: 0,
    max: 600_000,
    step: 100,
    keywords: 'idv kyc identity verification link telegram pre-app-redirect-url',
  },

  /* ---- After the win ---- */
  nheMin: {
    tab: 'after',
    scope: 'run',
    group: 'Orientation slot',
    label: 'Check no sooner than',
    summary:
      'After a shift is won the application waits for an orientation slot, asking at a random gap inside this range.',
    detail:
      'A range rather than one number because every applicant for a site is in that queue at once, so a single fixed gap has them all asking on the same tick — a herd and a signature in one.',
    unit: 's',
    kind: 'number',
    min: 1,
    max: 300,
    step: 1,
    keywords: 'nhe orientation new hire event slot poll jitter',
  },
  nheMax: {
    tab: 'after',
    scope: 'run',
    group: 'Orientation slot',
    label: '…and no later than',
    summary:
      'Waiting is not failing — a site can take hours to publish slots, and the wait itself has no deadline.',
    detail:
      'The server keeps the maximum at or above the minimum, so the two can be typed in either order without a moment where the range is inverted.',
    unit: 's',
    kind: 'number',
    min: 1,
    max: 300,
    step: 1,
    keywords: 'nhe orientation new hire event slot poll jitter',
  },
  watchdogMin: {
    tab: 'after',
    scope: 'run',
    group: 'Watchdog',
    label: 'Give up after no progress for',
    summary:
      'Measures the time since the last step finished, not since the start — so waiting for a slot never trips it, and a stalled connection does.',
    unit: 'min',
    zero: 'never',
    kind: 'number',
    min: 0,
    minNonZero: 1,
    max: 60,
    step: 1,
    keywords: 'watchdog onboarding stall abandon progress',
  },

  /* ---- Restarting after a deploy ---- */
  resumeStaggerMs: {
    tab: 'limits',
    scope: 'run',
    group: 'Resuming after a deploy',
    label: 'Gap between customer starts',
    summary:
      'When the server restarts a saved roster, this is how long it waits between one customer and the next.',
    detail:
      'A deploy stops every running customer, so the console keeps a roster of who was running and starts them again on one click. The starts are sequential whatever this is set to — the gap only decides how fast the queue behind the sign-in limit fills. Raising it spreads the same work over longer; lowering it does not raise the number of sign-ins running at once, which is capped separately. 0 = start them back-to-back.',
    unit: 'ms',
    zero: 'back-to-back',
    kind: 'number',
    min: 0,
    max: 60_000,
    step: 50,
    keywords: 'resume roster restart deploy stagger railway sessions save start',
  },

  /* ---- Sessions & limits ---- */
  maxHours: {
    tab: 'limits',
    scope: 'run',
    group: 'How long a run lives',
    label: 'Stop hunting after',
    summary: 'A hunt held open for days is fine — accounts and the session both refresh themselves.',
    detail:
      'The ceiling exists so a hunt cannot outlive the session it belongs to and keep polling with tokens that expired days earlier. A hunt that reaches it looks exactly like one that was stopped, which is why the form warns when it is set low. 0 = never stop.',
    unit: 'hours',
    zero: 'never',
    kind: 'number',
    min: 0,
    max: 72,
    step: 0.5,
    keywords: 'maxHuntMs ceiling stop give up duration',
  },
  keepAliveSec: {
    tab: 'limits',
    scope: 'run',
    group: 'Holding the seat',
    label: 'Re-confirm the seat every',
    summary:
      'One call per customer per interval, holding the seat between the application being filed and the shift opening.',
    detail:
      'Timed from the last response rather than the last send. It is also what keeps the session inside the ~60-second anti-bot window, so the claim itself never has to stop and solve a challenge. 0 turns the call off — and only the call: a running hunt keeps polling, keeps its pinned address, and still renews its session.',
    unit: 's',
    zero: 'off',
    kind: 'number',
    min: 0,
    minNonZero: 1,
    max: 3_600,
    step: 5,
    keywords: 'keep alive heartbeat beat seat hold anti-bot',
  },
  sessionMaxMin: {
    tab: 'limits',
    scope: 'run',
    group: 'Holding the seat',
    label: 'A sign-in lasts',
    summary:
      'How long a signed-in session keeps working before it must be re-minted. An observed figure, not one Amazon publishes.',
    detail:
      'NOT the life of the cookies a sign-in was built with — those are login material and are forgotten the moment login succeeds. What is left is the bearer token, which cannot be refreshed and whose expiry cannot be read, because Amazon’s access tokens are opaque blobs rather than JWTs. So this figure is learned by watching runs: lower it if sessions start being refused sooner. Raising it extends sessions already running.',
    unit: 'min',
    kind: 'number',
    min: 5,
    max: 720,
    step: 5,
    keywords: 'session lifetime bearer token expiry age',
  },
  sessionRenewMin: {
    tab: 'limits',
    scope: 'run',
    group: 'Holding the seat',
    label: '…and signs in again at',
    summary:
      'Re-mints a live session in the background while the current one still works, so workers never pause.',
    detail:
      'A shift opening mid-renewal is still claimed with the old token, and the new one is swapped in when it is ready. Left until something refuses the session instead, the same work costs a held hunt and a session that was already dead for up to a minute before anybody knew — at worst discovered on the claim itself. The server keeps this at least ten minutes below the lifetime above, so a slow sign-in always has runway to finish. 0 turns proactive renewal off and relies on the 401 path alone.',
    unit: 'min',
    zero: 'off',
    kind: 'number',
    min: 0,
    minNonZero: 1,
    max: 720,
    step: 5,
    keywords: 'session renew re-mint background sign in again',
  },
  logCalls: {
    tab: 'limits',
    scope: 'schedule',
    group: 'Call logs',
    label: 'Calls kept per schedule worker',
    summary: 'What each schedule worker keeps for the run drawer. The checks themselves, not a summary of them.',
    detail:
      'Every other buffer in the hunt answers a question ABOUT the checks — the newest few, the failures, the per-identity totals. This one IS the checks, per worker, so “show me what w3 has been doing” has an answer. The cost is workers × this × concurrent customers, in Redis at roughly 190 bytes a call: 200 at 5 workers and 500 customers is about 95 MB. Applied by the append itself, so lowering it takes effect on running hunts at their very next call.',
    unit: 'calls',
    zero: 'off',
    kind: 'number',
    min: 0,
    max: 1_000,
    step: 50,
    keywords: 'worker log depth redis drawer diagnostics',
  },
  gqlLogCalls: {
    tab: 'limits',
    scope: 'graphql',
    group: 'Call logs',
    label: 'Calls kept per GraphQL worker',
    summary: 'Kept much shallower than the schedule fleet’s, on purpose.',
    detail:
      'That fleet is higher-rate, and what you read off it is its hit and error RATIO rather than a minute of retained calls per worker. Every fleet stages into one Redis batcher that drains about 1,000 commands per 200ms, and this is a term of that budget which can be turned down without turning a fleet down.',
    unit: 'calls',
    zero: 'off',
    kind: 'number',
    min: 0,
    max: 1_000,
    step: 25,
    keywords: 'graphql shift list log depth redis diagnostics',
  },
}

/** Every key on one tab, in registry order. */
export function keysForTab(tab: TabId): FieldKey[] {
  return (Object.keys(FIELD_DEFS) as FieldKey[]).filter((k) => FIELD_DEFS[k].tab === tab)
}

/**
 * Search across every field, on a page where scrolling six tabs to find one
 * number is the ordinary case.
 *
 * Matches on the label, the summary, the long detail, the card it lives in and
 * an explicit keyword list — so `429`, `capsolver`, `postingStatus` and `waf`
 * all land somewhere, even though none of them appear in a label. Every term
 * has to match, which is what makes a two-word query narrow rather than widen
 * the result.
 */
export function searchFields(query: string): FieldKey[] {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0)
  if (terms.length === 0) return []

  return (Object.keys(FIELD_DEFS) as FieldKey[]).filter((k) => {
    const m = FIELD_DEFS[k]
    // `only` is in the haystack so searching "brazil" returns every field the
    // Brazil flow reads, not just the one that names it.
    const hay = `${m.label} ${m.summary} ${m.detail ?? ''} ${m.group} ${m.unit ?? ''} ${
      m.keywords ?? ''
    } ${m.only ?? ''} ${k}`.toLowerCase()
    return terms.every((t) => hay.includes(t))
  })
}
